import api from './axiosConfig';

/**
 * Fetch featured businesses for the marketplace
 */
export const getFeaturedBusinesses = async () => {
    try {
        const response = await api.get('/marketplace/featured');
        return response.data;
    } catch (error) {
        console.error('Error fetching featured businesses:', error);
        throw error.response?.data?.error || 'Failed to fetch businesses';
    }
};

/**
 * Search businesses with query and category
 */
export const searchBusinesses = async (query = '', category = '') => {
    try {
        const response = await api.get('/marketplace/search', {
            params: { query, category }
        });
        return response.data;
    } catch (error) {
        console.error('Error searching businesses:', error);
        throw error.response?.data?.error || 'Search failed';
    }
};

/**
 * Get full details for a specific business
 */
export const getBusinessDetails = async (id) => {
    try {
        const response = await api.get(`/marketplace/details/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching business details:', error);
        throw error.response?.data?.error || 'Failed to fetch details';
    }
};

/**
 * List a new business (supports image upload)
 */
export const listBusiness = async (formData) => {
    try {
        // Ensure we handle multiple photos if they exist in a special way if needed,
        // but FormData.append handles multiple values for the same key.
        const response = await api.post('/marketplace/list', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error listing business:', error);
        throw error.response?.data?.error || 'Listing failed';
    }
};

/**
 * Add a product or service to a business listing
 */
export const addMarketplaceItem = async (formData) => {
    try {
        const response = await api.post('/marketplace/items/add', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error adding marketplace item:', error);
        throw error.response?.data?.error || 'Failed to add item';
    }
};

/**
 * Update an existing marketplace item
 */
export const updateMarketplaceItem = async (itemId, formData) => {
    try {
        const response = await api.put(`/marketplace/items/${itemId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating marketplace item:', error);
        throw error.response?.data?.error || 'Failed to update item';
    }
};

/**
 * Delete a marketplace item
 */
export const deleteMarketplaceItem = async (itemId) => {
    try {
        const response = await api.delete(`/marketplace/items/${itemId}`);
        return response.data;
    } catch (error) {
        console.error('Error deleting marketplace item:', error);
        throw error.response?.data?.error || 'Failed to delete item';
    }
};

/**
 * Get categories (mocked for now as we don't have a categories table yet)
 */
export const getMarketplaceCategories = async () => {
    return [
        { id: 1, name: 'Food', icon: 'restaurant', color: '#EF4444' },
        { id: 2, name: 'Fashion', icon: 'shirt', color: '#EC4899' },
        { id: 3, name: 'Tech', icon: 'laptop', color: '#6366F1' },
        { id: 4, name: 'Agri', icon: 'leaf', color: '#10B981' },
        { id: 5, name: 'Crafts', icon: 'color-palette', color: '#F59E0B' },
        { id: 6, name: 'Trades', icon: 'hammer', color: '#8B5CF6' },
    ];
};

/**
 * Checkout / Create Order
 */
export const createOrder = async (orderData) => {
    try {
        const response = await api.post('/orders/checkout', orderData);
        return response.data;
    } catch (error) {
        console.error('Error creating order:', error);
        throw error.response?.data?.error || 'Checkout failed';
    }
};

/**
 * Get My Orders (As Buyer)
 */
export const getMyOrders = async () => {
    try {
        const response = await api.get('/orders/buying');
        return response.data;
    } catch (error) {
        console.error('Error fetching orders:', error);
        throw error.response?.data?.error || 'Failed to fetch orders';
    }
};

/**
 * Get My Sales (As Seller)
 */
export const getMySales = async () => {
    try {
        const response = await api.get('/orders/selling');
        return response.data;
    } catch (error) {
        console.error('Error fetching sales:', error);
        throw error.response?.data?.error || 'Failed to fetch sales';
    }
};

export const confirmOrderReceipt = async (orderId, rating, feedback) => {
    try {
        const response = await api.put(`/orders/${orderId}/confirm-receipt`, { rating, feedback });
        return response.data;
    } catch (error) {
        console.error('Error confirming receipt:', error);
        throw error.response?.data?.error || 'Failed to confirm receipt';
    }
};

/**
 * Cancel Order
 */
export const cancelOrder = async (orderId) => {
    try {
        const response = await api.post(`/orders/${orderId}/cancel`);
        return response.data;
    } catch (error) {
        console.error('Error cancelling order:', error);
        throw error.response?.data?.error || 'Failed to cancel order';
    }
};

/**
 * Update Order Status (For Vendors)
 */
export const updateOrderStatus = async (orderId, status) => {
    try {
        const response = await api.put(`/orders/${orderId}/status`, { status });
        return response.data;
    } catch (error) {
        console.error('Error updating order status:', error);
        throw error.response?.data?.error || 'Failed to update order status';
    }
};

/**
  * Get Wallet Balance & Transactions
 */
export const getWalletBalance = async () => {
    try {
        const response = await api.get('/wallet/balance');
        return response.data;
    } catch (error) {
        console.error('Error fetching wallet:', error);
        throw error.response?.data?.error || 'Failed to fetch wallet';
    }
};

/**
 * Request Withdrawal
 */
export const requestWithdrawal = async (amount, phone) => {
    try {
        const response = await api.post('/wallet/withdraw', { amount, phone });
        return response.data;
    } catch (error) {
        console.error('Error requesting withdrawal:', error);
        throw error.response?.data?.error || 'Withdrawal failed';
    }
};

/**
 * Service Bookings
 */
export const bookService = async (bookingData) => {
    try {
        const response = await api.post('/marketplace/services/book', bookingData);
        return response.data;
    } catch (error) {
        console.error('Error booking service:', error);
        throw error.response?.data?.error || 'Booking failed';
    }
};

export const getMyBookings = async (type = 'customer') => {
    try {
        const response = await api.get('/marketplace/services/bookings', {
            params: { type }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching bookings:', error);
        throw error.response?.data?.error || 'Failed to fetch bookings';
    }
};

